import React from "react";
import {
  Box,
  Stack,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useTranslation } from "react-i18next";

const ChannelBoardToolbar = ({ stats, onOpenPopup, onOpenAnalytics }) => {
  const { t } = useTranslation("teamTasks");
  const theme = useTheme();
  const { openCount = 0, doneCount = 0, totalCount = 0 } = stats || {};

  return (
    <Box
      sx={{
        flexShrink: 0,
        px: { xs: 1.25, sm: 2 },
        py: { xs: 1, sm: 1.25 },
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        bgcolor: alpha(theme.palette.background.paper, 0.72),
        backdropFilter: "blur(8px)",
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: "block", mb: 0.5 }}>
          {t("channelBoardToolbarHint")}
        </Typography>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {totalCount === 0 ? (
            <Chip
              size="small"
              icon={<AssignmentOutlinedIcon sx={{ fontSize: "14px !important" }} />}
              label={t("channelBoardEmptyBoard")}
              sx={{ height: 24, fontWeight: 600, fontSize: "0.72rem" }}
            />
          ) : (
            <>
              <Chip
                size="small"
                color={openCount > 0 ? "primary" : "default"}
                variant={openCount > 0 ? "filled" : "outlined"}
                icon={<AssignmentOutlinedIcon sx={{ fontSize: "14px !important" }} />}
                label={t("channelBoardOpenTasks", { count: openCount })}
                sx={{ height: 24, fontWeight: 700, fontSize: "0.72rem" }}
              />
              {doneCount > 0 ? (
                <Chip
                  size="small"
                  variant="outlined"
                  color="success"
                  icon={<CheckCircleOutlineIcon sx={{ fontSize: "14px !important" }} />}
                  label={t("channelBoardDoneTasks", { count: doneCount })}
                  sx={{ height: 24, fontWeight: 600, fontSize: "0.72rem" }}
                />
              ) : null}
            </>
          )}
        </Stack>
      </Box>

      <Stack direction="row" spacing={0.5} flexShrink={0}>
        {onOpenAnalytics ? (
          <Tooltip title={t("boardAnalytics")}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<InsightsOutlinedIcon />}
              onClick={onOpenAnalytics}
              sx={{ fontWeight: 600, borderRadius: 2 }}
            >
              {t("boardAnalytics")}
            </Button>
          </Tooltip>
        ) : null}
        <Tooltip title={t("openInPopupHint")}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<OpenInNewOutlinedIcon />}
            onClick={onOpenPopup}
            sx={{
              display: { xs: "none", sm: "inline-flex" },
              fontWeight: 600,
              borderRadius: 2,
              textTransform: "none",
              borderColor: alpha(theme.palette.divider, 0.8),
            }}
          >
            {t("openInPopup")}
          </Button>
        </Tooltip>
        <Tooltip title={t("openInPopup")}>
          <IconButton
            size="small"
            onClick={onOpenPopup}
            sx={{
              display: { xs: "inline-flex", sm: "none" },
              border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
              borderRadius: 2,
            }}
          >
            <OpenInNewOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
};

export default ChannelBoardToolbar;
